import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import type { FormlyFieldConfig } from '@ngx-formly/core';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { StatusBadgeComponent } from '../../../status-badge.component';
import type { StatusBadgeMappingItem } from '../../../status-badge.types';
import { resolveStatusBadge, type ResolvedStatusBadge } from '../../../../utils/status-badge-resolver.util';
import { isStatusColumnId, manualOptionsFrom, optionBadgesFrom } from '../../../table-column-resolution.util';
import type { LinkedEntryEnvelope } from '../readonly.types';
import { LinkedEntryDisplayComponent } from './linked-entry-display.component';
import { ValueDisplayComponent } from './value-display.component';

/** The module-level status configuration the read row reads off `formState.entity` (#20811). */
interface StatusEntity {
  statusKeyPath?: string;
  statusBadgeMapping?: StatusBadgeMappingItem[];
}

/**
 * Read display for option fields (#21239) — three outcomes, decided in this order.
 *
 * 1. THE MODULE STATUS FIELD (the one `statusKeyPath` names) gets the badge the portal already
 *    shows for it everywhere else: the detail header (`DetailsService.statusBadge`) and the list
 *    column (`table.component.ts`) resolve the same admin-configured `statusBadgeMapping`, so a
 *    status cannot look green in the header and read as a bare word in the read view. Only the
 *    configured status field: matching the mapping against every option field would badge any
 *    other field that happens to store "actief". It stays on this path whatever its value looks
 *    like — a status is never a way out of the page, so it never becomes a link.
 *    The same goes for an option field the admin switched to "Toon als badge in kolommen"
 *    (`props.statusBadge`): its list column already badges it with the module's status colors
 *    (`table.component.ts#statusBadgeFor` with `forceMapping`), so the read row does too — with
 *    that column's legacy pending/approved/rejected/archived fallback, so both agree on every value.
 * 2. AN ENVELOPE ANSWER on any OTHER option field is a reference to an entry in another module —
 *    what a module-backed option set stores (`moduleValueType: 'entry'`) — so it is handed to
 *    `LinkedEntryDisplayComponent` and reads as a link to that item. Clicking through is the
 *    default since the user decision of 2026-09-10 (task 21258b): before that it needed a
 *    per-field `props.readonlyDisplay: 'linked-entry'` override, because `select`/`custom-select`
 *    already had their registry slot taken by THIS display. The linked display decides for itself
 *    whether it can build a route and otherwise renders exactly the same text as outcome 3.
 * 3. EVERYTHING ELSE is the option LABEL as plain text, through the one shared text rule.
 *
 * Presentational: the module configuration arrives through `field.options.formState`, which
 * `DetailsService` publishes on every entry form — no store is injected, so a story renders
 * this with plain inputs.
 */
@Component({
  selector: 'dd-status-display',
  imports: [StatusBadgeComponent, LinkedEntryDisplayComponent, ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — the display must not become a box inside the value span, or the row's
  // truncate/multiline behaviour would stop at this element.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    @if (badge(); as badge) {
      <app-status-badge [label]="badge.label" [variant]="badge.variant" />
    } @else if (isLinkedEntry()) {
      <dd-linked-entry-display [field]="field()" [value]="value()" />
    } @else {
      <dd-value-display [field]="field()" [value]="value()" />
    }
  `,
})
export class StatusDisplayComponent extends ReadonlyDisplayBase {
  /** True only for the field the module points at with `statusKeyPath` (outcome 1 above). */
  readonly #isModuleStatusField = computed(() => {
    const field = this.field();
    const entity = (field.options?.formState as { entity?: StatusEntity } | undefined)?.entity;
    return !!entity?.statusKeyPath && isStatusColumnId(fieldKeyPath(field), entity.statusKeyPath);
  });

  /** The admin's "Toon als badge in kolommen" toggle on this option field (#20811). */
  readonly #isBadgeField = computed(() => this.field().props?.['statusBadge'] === true);

  /**
   * The badge, or `undefined` for "this is not a badge field, or its value was never mapped".
   * No `fallbackVariant` here — an unmapped value in a read row is better read as itself than as
   * a neutral pill (the header opts into that fallback because a header badge is a fixed slot).
   */
  readonly badge = computed(() => {
    const isStatusField = this.#isModuleStatusField();
    if (!isStatusField && !this.#isBadgeField()) {
      return undefined;
    }
    const entity = (this.field().options?.formState as { entity?: StatusEntity } | undefined)?.entity;
    // The badge field mirrors its list column, legacy fallback included, optionVariants
    // EXCLUDED — the list's own forced-badge column (`table.component.ts` `statusBadgeFor(...,
    // forceMapping: true)`) does not resolve manual-option colour either, so this stays mirrored
    // to that instead of gaining a tone the column itself does not have. The module status field
    // keeps reading an unmapped value as itself (see the doc comment above), but now checks its
    // own manual-option colour before giving up (#21239 T4) — the same tone the list and the
    // detail header already resolve for this field.
    return resolveStatusBadge(this.value(), entity?.statusBadgeMapping, {
      legacyFallback: !isStatusField,
      optionVariants: isStatusField ? this.#optionBadges() : undefined,
    });
  });

  /**
   * Manual-option colour for THIS field, pre-resolved to `{ label, variant }` badges keyed by
   * option value (#21239 T4) — the map-building itself is `optionBadgesFrom`
   * (table-column-resolution.util.ts, code review round: was a hand-rolled copy here). Reads
   * `field().props.optionsArray` directly rather than looking the field up elsewhere: this
   * component already IS the status field when it is the module status field, unlike
   * `TableComponent`/`DetailsService`, which resolve an arbitrary column/keyPath first.
   */
  readonly #optionBadges = computed<ReadonlyMap<string, ResolvedStatusBadge> | undefined>(() =>
    optionBadgesFrom(manualOptionsFrom(this.field().props?.['optionsArray']))
  );

  /** Outcome 2: an entry reference on a field that is not the module's status field. */
  readonly isLinkedEntry = computed(
    () => !this.#isModuleStatusField() && !this.#isBadgeField() && holdsEntryEnvelope(this.value())
  );
}

/**
 * True when the answer carries at least one ENVELOPE — an object with an entry id or a nested
 * entry, the shape a module-backed option set stores (dynamic-options.service.ts:632-647) and the
 * only one a route can be built from. A taxonomy answer (`{ slug, selectedInput }`) and a plain
 * option code have neither, so they stay on the text display.
 *
 * A false positive here would still be harmless — `LinkedEntryDisplayComponent` falls back to the
 * same `dd-value-display` when it cannot place an item — but keeping the common case off that
 * path avoids a component layer per plain option row.
 */
function holdsEntryEnvelope(value: unknown): boolean {
  const items = Array.isArray(value) ? value : [value];
  return items.some(item => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return false;
    }
    const envelope = item as LinkedEntryEnvelope;
    return typeof envelope.id === 'string' || !!envelope.entry;
  });
}

/**
 * The path a generated field carries (`props.keyPath`, "entry.status"), or its key. Compared with
 * `statusKeyPath` ("status", "general.status") by `isStatusColumnId` — the table's own rule, which
 * tolerates the `entry.` prefix on either side, so the read row and the list column agree on
 * WHICH field is the status field.
 */
function fieldKeyPath(field: FormlyFieldConfig): string {
  return (field.props?.['keyPath'] as string | undefined) ?? String(field.key ?? '');
}
